import React from "react";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Check, ArrowRight } from "lucide-react";
import { COMPANY, IMAGES } from "../mock";

const principles = [
  { num: "01", title: "Integrity", text: "Honest advice, transparent communication and realistic valuations." },
  { num: "02", title: "Professionalism", text: "High standards of marketing, negotiation and compliance." },
  { num: "03", title: "Proactive Service", text: "Regular updates, responsive communication and attention to detail from instruction to completion." },
];

const whyChoose = [
  "Deep knowledge of the Bath & Somerset property market",
  "Professional marketing strategies",
  "Experienced negotiation and sales progression",
  "Comprehensive lettings and property management services",
  "Transparent, client-focused approach",
];

const About = () => {
  return (
    <div>
      <Seo title="About Anadear Estates — Bath's Independent Estate Agents" description="Meet Anadear Estates: an independent, Bath-born estate agency delivering a personal, expert service in sales, lettings and property management." path="/about-us" />
      <section className="relative h-[360px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img src={IMAGES.about} alt="About Anadear Estates" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center px-5">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#e8d4b8] mb-3">Bath &amp; Bristol</p>
          <h1 className="font-display text-white text-[40px] md:text-[56px] font-300">About Anadear Estates</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <img
            src="https://images.unsplash.com/photo-1595320078268-43862afaa9a5"
            alt="Estate Agents Bath"
            className="rounded-2xl w-full h-[460px] object-cover shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
          />
          <div>
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Who We Are</p>
            <h2 className="font-display text-[30px] md:text-[38px] font-300 text-[#2b2b2b] leading-tight mb-6">
              Your Trusted Property Experts in Bath &amp; Bristol
            </h2>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-5">
              At Anadear Estates, we are passionate about property and committed to delivering
              exceptional service across Bath and Bristol. With extensive knowledge of the local
              market, we provide professional property sales, lettings and management services
              tailored to the needs of homeowners, landlords, buyers and tenants.
            </p>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-8">
              Our approach combines traditional estate agency values with modern technology,
              ensuring every client experience is transparent, efficient and results-driven.
            </p>
            <div className="flex gap-3">
              <a href={COMPANY.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#faf7f2] border border-[#e2d8c8] flex items-center justify-center text-[#b8935f] hover:bg-[#b8935f] hover:text-white transition-colors">
                <Instagram size={18} />
              </a>
              <a href={COMPANY.linkedin} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#faf7f2] border border-[#e2d8c8] flex items-center justify-center text-[#b8935f] hover:bg-[#b8935f] hover:text-white transition-colors">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#faf7f2]">
        <div className="max-w-[860px] mx-auto px-5 text-center">
          <h2 className="font-display text-[30px] md:text-[38px] font-300 text-[#2b2b2b] mb-6">
            Local Knowledge. Personal Service.
          </h2>
          <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-5">
            The Bath property market is unique — from elegant Georgian townhouses to modern
            apartments and countryside homes across Somerset. Our team understands the nuances of
            local demand, pricing trends and buyer behaviour, allowing us to deliver informed
            advice and strategic marketing.
          </p>
          <p className="text-[15px] text-[#6a6a6a] leading-relaxed">
            At Anadear Estates, we believe property is personal. Whether you're selling your home,
            letting an investment property or searching for your next move, we take the time to
            understand your goals and guide you with confidence.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Our Values</p>
            <h2 className="font-display text-[30px] md:text-[40px] font-300 text-[#2b2b2b]">
              We are built on three core principles
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {principles.map((p) => (
              <div key={p.num} className="p-8 rounded-2xl border border-[#f0ece5] hover:border-[#b8935f]/40 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all">
                <span className="font-display text-[44px] font-300 text-[#e2d0b5]">{p.num}</span>
                <h3 className="font-display text-[22px] font-500 text-[#2b2b2b] mt-3 mb-3">{p.title}</h3>
                <p className="text-[14px] text-[#6a6a6a] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#faf7f2]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">The Anadear Difference</p>
            <h2 className="font-display text-[30px] md:text-[40px] font-300 text-[#2b2b2b] mb-8">
              Why Choose Anadear Estates?
            </h2>
            <ul className="space-y-4 mb-8">
              {whyChoose.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#b8935f] flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-white" />
                  </span>
                  <span className="text-[15px] text-[#4a4a4a]">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-8">
              Whether you're looking to sell, let, buy or invest, Anadear Estates offers expert
              guidance and dedicated support every step of the way.
            </p>
            <Link to="/services#valuation" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">
              Get an Instant Valuation <ArrowRight size={17} />
            </Link>
          </div>
          <img
            src="https://images.unsplash.com/photo-1740596400206-f894025300b5"
            alt="estate agents Bath"
            className="rounded-2xl w-full h-[500px] object-cover shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
          />
        </div>
      </section>
    </div>
  );
};

export default About;
